import { Component, model, input, effect, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { LineItemModel } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { ValidationService } from '../../../../services/validation.service';

@Component({
  selector: 'app-datetimepicker-control',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePicker],
  template: `
    <div class="checklist-control-row">
      <label [for]="'datetimepicker-' + lineItem().lineItemId" class="checklist-control-label">
        {{ lineItem().name }}@if (lineItem().isRequired) { <span class="required-indicator">*</span>}
      </label>
      <div class="checklist-control-input">
        <p-datepicker
          [inputId]="'datetimepicker-' + lineItem().lineItemId"
          [(ngModel)]="dateTimeValue"
          (ngModelChange)="onDateTimeChange($event)"
          [disabled]="disabled()"
          [invalid]="hasError()"
          [showIcon]="true"
          [showTime]="true"
          hourFormat="12"
          iconDisplay="input"
          dateFormat="yy-mm-dd"
          placeholder="Select date & time..."
          appendTo="body"
          [fluid]="true"
          (onBlur)="onBlur()"
        />
        @if (hasError()) {
          @for (err of errors(); track $index) {
            <small class="p-error d-block">{{ err }}</small>
          }
        } @else if (hint()) {
          <small class="text-color-secondary">{{ hint() }}</small>
        }
      </div>
      <div class="ctrl-speaker-col">
        <button type="button" class="ctrl-speaker-btn" (click)="speakLabel()"
          [attr.aria-label]="'Read label: ' + lineItem().name">
          <i class="ri-volume-up-line"></i>
        </button>
      </div>
    </div>
  `,
  styles: []
})
export class DatetimepickerControlComponent {
  lineItem = input.required<LineItemModel>();
  value = model<any>(null);
  disabled = input<boolean>(false);
  showError = input<boolean>(false);

  dateTimeValue: Date | null = null;

  private speechService = inject(SpeechService);
  private validationService = inject(ValidationService);
  touched = signal(false);

  errors = computed(() => {
    if (!this.showError() && !this.touched()) return [];
    return this.validationService.validate(this.lineItem(), this.value());
  });

  hasError = computed(() => this.errors().length > 0);
  hint = computed(() => this.hasError() ? '' : this.validationService.getHint(this.lineItem()));

  constructor() {
    // Convert string dates to Date objects when value changes from parent
    effect(() => {
      const val = this.value();
      if (val) {
        // If it's a string, convert to Date
        if (typeof val === 'string') {
          this.dateTimeValue = new Date(val);
        } else if (val instanceof Date) {
          this.dateTimeValue = val;
        } else {
          this.dateTimeValue = null;
        }
      } else {
        this.dateTimeValue = null;
      }
    });
  }

  onDateTimeChange(dateTime: Date | null): void {
    // Sync the datetime change back to the value model
    this.value.set(dateTime);
  }

  onBlur(): void {
    this.touched.set(true);
  }

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }
}
